/**
 * @fileoverview Own authenticated route composition and document-workspace lifetime.
 *
 * `AuthContext.js` owns browser session identity; `AppAccessContext.js` owns
 * workspace availability and the home redirect for inaccessible locations.
 * Domain pages own their actions/state and backend endpoints own operation
 * authorization.
 *
 * `components/Documents/DocumentsPage.js` and
 * `components/DocumentChat/DocumentChatPage.js` mount on first visit, when the
 * workspace is accessible, and then stay mounted while hidden. Route visibility
 * therefore differs from component lifetime: navigation can preserve local work,
 * and hidden workspaces must still respect identity and subscription boundaries.
 * Change persistence policy here when a workspace should survive navigation or
 * reset. The local feature-proposals route exists only in development builds,
 * behind `localFeatureProposalsFeature.js`.
 */

import React, { Suspense, useContext, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import './App.css';
import CaseDetails from "./components/CaseDetails/CaseDetails";
import SidebarLayout from "./components/SidebarLayout";
import ToastProvider from './ToastProvider';
import Settings from "./components/Settings";
import Login from "./components/Login";
import Onboarding from "./components/Onboarding";
import { AuthProvider } from './AuthContext';
import { AuthContext } from './AuthContext';
import { AppAccessProvider, useAppAccess } from './AppAccessContext';
import Admin from "./components/Admin/Admin";
import Utilities from "./components/Utilities/Utilities";
import SimulArchive from "./components/Utilities/SimulArchive";
import SignatureGenerator from "./components/Utilities/SignatureGenerator/SignatureGenerator";
import { BlockUI } from "./components/shared/AppLoadingOverlay";
import { ProgressSpinner } from "./components/shared/AppSpinner";
import { useSelector } from "react-redux";
import NotFoundPage from "./components/NotFoundPage";
import AllTrelloTasks from "./components/Utilities/AllTrelloTasks";
import Calendar from "./components/Calendar/Calendar";
import DocumentsPage from "./components/Documents/DocumentsPage";
import Automations from "./components/Automations/Automations";
import LogsPage from "./components/Logs/LogsPage";
import DriveDedup from "./components/DriveDedup/DriveDedup";
import { getStoredPostLoginRedirectPath } from "./authRedirect";
import NewMattersTable from "./components/Matters/NewMattersTable/NewMattersTable";
import IntakePage from "./components/Intake/IntakePage";
import IntakeCommunications from "./components/Intake/IntakeCommunications";
import IntakeLeadDetail from "./components/Intake/IntakeLeadDetail";
import { IntakeAttorneyReviews } from "./components/Intake/IntakeAttorneyReviews";
import IntakeBatches from "./components/Intake/IntakeBatches";
import TaskInbox from "./components/Inbox/TaskInbox";
import PortalResult from "./components/Inbox/PortalResult";
import { isLocalFeatureProposalsEnabled } from "./localFeatureProposalsFeature";

const DocumentChatPage = React.lazy(() => import("./components/DocumentChat/DocumentChatPage"));
const SyncPage = React.lazy(() => import("./components/DocumentChat/components/Files/SyncPage"));
const DraftingRunsPage = React.lazy(() => import("./components/DraftingRuns/DraftingRunsPage"));
// LOCAL-ONLY FEATURE PROPOSALS: compiled out of production builds (no chunk is
// emitted); also gated at runtime by isLocalFeatureProposalsEnabled().
const LocalFeatureProposals = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import("./components/LocalFeatureProposals/LocalFeatureProposals"))
  : null;



function RouteSuspense({ children }) {
  return (
    <Suspense fallback={<ProgressSpinner />}>
      {children}
    </Suspense>
  );
}



function PersistentRoutePage({ active, children }) {
  const hasBeenActiveRef = useRef(false);

  if (active) {
    hasBeenActiveRef.current = true;
  }

  if (!hasBeenActiveRef.current) {
    return null;
  }

  return (
    <div className="persistent-route-cache" hidden={!active}>
      {children}
    </div>
  );
}



function ProtectedLayout({ pageComponent }) {
  const { isAuthenticated, requiresOnboarding } = useContext(AuthContext);
  const { canAccess, canAccessLocation, homePath } = useAppAccess();
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isDocumentsRoute = normalizedPath === '/documents' || normalizedPath.startsWith('/documents/');
  const isDocumentChatRoute = normalizedPath === '/document_chat';
  const isPersistentRoute = isDocumentsRoute || isDocumentChatRoute;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiresOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (normalizedPath !== '/admin' && !canAccessLocation(location)) {
    return <Navigate to={normalizedPath === '/settings' ? '/settings?tab=personal' : homePath} replace />;
  }

  return (
    <SidebarLayout>
      {canAccess('documents') && <PersistentRoutePage active={isDocumentsRoute}>
        <DocumentsPage />
      </PersistentRoutePage>}
      {canAccess('assistant') && <PersistentRoutePage active={isDocumentChatRoute}>
        <RouteSuspense><DocumentChatPage /></RouteSuspense>
      </PersistentRoutePage>}
      {!isPersistentRoute && (pageComponent || <Outlet />)}
    </SidebarLayout>
  );
}



function OnboardingRoute() {
  const { isAuthenticated, requiresOnboarding } = useContext(AuthContext);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!requiresOnboarding) {
    return <Navigate to={getStoredPostLoginRedirectPath()} replace />;
  }

  return <Onboarding />;
}



function AdminRoute() {
  const { isAdmin } = useContext(AuthContext);

  if (!isAdmin) {
    return <Navigate to="/settings" replace />;
  }

  return <Admin />;
}



function App() {
  const appLoading = useSelector(state => state.app.loading);
  // Literal NODE_ENV check lets production builds drop the route entirely.
  const localFeatureProposalsEnabled = process.env.NODE_ENV === 'development'
    && Boolean(LocalFeatureProposals) && isLocalFeatureProposalsEnabled();

  return (
    <AuthProvider>
      <AppAccessProvider>
      <ToastProvider>
        <BlockUI blocked={appLoading} fullScreen template={
          <ProgressSpinner />
        } />

        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingRoute />} />
            <Route element={<ProtectedLayout />}>
              <Route index element={<NewMattersTable />} />
              <Route path="matters" element={<NewMattersTable />} />
              <Route path="new-matters-table" element={<Navigate to="/matters" replace />} />
              <Route path="intake" element={<Navigate to="/intake/communications" replace />} />
              <Route path="intake/communications" element={<IntakeCommunications />} />
              <Route path="intake/leads" element={<IntakePage />} />
              <Route path="intake/reviews" element={<IntakeAttorneyReviews />} />
              <Route path="intake/reviews/:reviewId" element={<IntakeAttorneyReviews />} />
              <Route path="intake/batches" element={<IntakeBatches />} />
              <Route path="intake/batches/:batchId" element={<IntakeBatches />} />
              <Route path="intake/leads/:leadId" element={<IntakeLeadDetail />} />
              <Route path="inbox" element={<TaskInbox />} />
              <Route path="payment-instructions/:reviewId" element={<PortalResult />} />
              <Route path="case_details/:MatterNumber" element={<CaseDetails />} />
              <Route path="document_chat" element={null} />
              <Route path="settings" element={<Settings />} />
              <Route path="utilities" element={<Utilities />} />
              <Route path="utilities/signature-generator" element={<SignatureGenerator />} />
              <Route path="utilities/simul-archive" element={<SimulArchive />} />
              <Route path="admin" element={<AdminRoute />} />
              <Route path="trello_tasks" element={<AllTrelloTasks />} />
              <Route path="automations" element={<Automations />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="calendar/event/:eventId" element={<Calendar />} />
              <Route path="utilities/drive-dedup" element={<DriveDedup />} />
              <Route path="documents" element={null} />
              <Route path="documents/:documentId" element={null} />
              <Route path="sync" element={<RouteSuspense><SyncPage /></RouteSuspense>} />
              <Route path="drafting-runs" element={<RouteSuspense><DraftingRunsPage /></RouteSuspense>} />
              {localFeatureProposalsEnabled ? (
                <Route path="local/feature-proposals" element={<RouteSuspense><LocalFeatureProposals /></RouteSuspense>} />
              ) : null}
            </Route>
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </ToastProvider>
      </AppAccessProvider>
    </AuthProvider >
  );
}

export default App;
